import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../redux/slices/appInfo';
import {
    Direction,
    IPagination,
    SearchQueryParams,
} from '../../interface/paging';
import { DEFAULT_PAGE_SIZE } from '../../utils/constants';
import { SearchParamsStateType, useFilter } from '../../hooks/useFilter';
import { removeUndefinedAttribute } from '../../utils';
import { useNotification } from '../../hooks/useNotification';
import { getInvoice } from '../../api/invoice';
import { TInvoiceRequest } from '../../api/invoice/type';
import { FOLIOS_MODE, TInvoice } from '../components/folios/type';
import { FoliosPageList } from '../components/folios/list';
import { SOURCE_PAGE } from '../../utils/enums';

const Wrapper = styled.div``;

type TSortData = {
    field?: string;
    order?: 'asc' | 'desc';
};
type TSortDataContext = {
    sortData: TSortData;
    setSortData: (data: TSortData) => void;
};
const SortDataContext = React.createContext<TSortDataContext>(
    {} as TSortDataContext
);
export const useSortData = () => React.useContext(SortDataContext);

type SubmittedFoliosPageProps = {
    source: SOURCE_PAGE;
};

export const SubmittedFoliosPage: React.FC<SubmittedFoliosPageProps> = ({
    source,
}) => {
    const dispatch = useDispatch();
    const { showError } = useNotification();
    const filtersDefaults: SearchParamsStateType = {
        page: { type: 'number', default: 1 },
        pageSize: { type: 'number', default: DEFAULT_PAGE_SIZE },
        sortBy: { type: 'string', default: Direction.DESC },
        orderBy: { type: 'string', default: 'created_on' },
        filter: { type: 'string', default: '' },
        search: { type: 'string', default: '' },
    };
    const [filterParams, setFilterParams] = useFilter(filtersDefaults);
    const [pagination, setPagination] = React.useState<IPagination | undefined>(
        undefined
    );
    const [sortData, setSortData] = React.useState<TSortData>({
        field: 'created_on',
        order: 'desc',
    });
    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>(
        []
    );

    const onChangeSort = React.useCallback(
        (data: TSortData) => {
            setSortData(data);
            const searchQueryParams: SearchQueryParams = {
                page: 1,
                pageSize: filterParams?.pageSize || DEFAULT_PAGE_SIZE,
                sortBy: data.order as Direction,
                orderBy: data.field,
            };
            onLoadData(searchQueryParams);
        },
        [filterParams]
    );
    const [invoiceList, setInvoiceList] = React.useState<TInvoice[]>([]);

    const onLoadData = React.useCallback(
        async (queryParams: SearchQueryParams | undefined) => {
            try {
                dispatch(setLoading(true));
                const payloadRequest: TInvoiceRequest = {
                    ...queryParams,
                    source,
                    filter: queryParams?.filter || 'status:eq:SUBMITTED',
                    search: queryParams?.search,
                    page: queryParams?.page ?? 1,
                    pageSize: undefined,
                    created_on: undefined,
                    per_page: queryParams?.pageSize ?? DEFAULT_PAGE_SIZE,
                };
                const newPayload = removeUndefinedAttribute(payloadRequest);
                setFilterParams(newPayload);
                setSelectedRowKeys([]);
                const result = await getInvoice(newPayload);
                if (result.status === 200) {
                    setInvoiceList(result.data?.data?.invoices ?? []);
                    setPagination({
                        page: result.data?.data?.page ?? 1,
                        pageSize:
                            result.data?.data?.pageSize ?? DEFAULT_PAGE_SIZE,
                        totalCount: result.data?.data?.totalCount ?? 0,
                        totalPage: result.data?.data?.totalPage ?? 0,
                        order: newPayload?.sortBy as Direction,
                        orderBy: newPayload?.orderBy,
                    });
                }
            } catch (error: any) {
                showError('Error', error?.message);
            } finally {
                dispatch(setLoading(false));
            }
        },
        [dispatch, source, showError]
    );

    const onChangePaging = React.useCallback(
        (values: any) => {
            const searchQueryParams: SearchQueryParams = {
                page: values.page,
                pageSize: pagination?.pageSize ?? 10,
                sortBy: pagination?.order as Direction,
                orderBy: pagination?.orderBy ?? 'created_on',
            };
            onLoadData(searchQueryParams);
        },
        [pagination]
    );

    React.useEffect(() => {
        onLoadData({
            page: 1,
            pageSize: filterParams?.pageSize || DEFAULT_PAGE_SIZE,
            sortBy: Direction.DESC,
            orderBy: 'created_on',
        });
    }, [source]);

    const onReload = React.useCallback(() => {
        onLoadData({
            page: 1,
            pageSize: filterParams?.pageSize || DEFAULT_PAGE_SIZE,
            sortBy: filterParams.order as Direction,
            orderBy: filterParams.orderBy,
        });
    }, [filterParams]);

    return (
        <Wrapper className="flex flex-col items-center justify-start h-full overflow-hidden">
            <div className="flex flex-row items-start h-full w-full">
                <SortDataContext.Provider
                    value={{ sortData, setSortData: onChangeSort }}
                >
                    <FoliosPageList
                        source={source}
                        invoiceList={invoiceList}
                        pagination={pagination}
                        foliosMode={FOLIOS_MODE.SUBMITTED}
                        onChangePaging={onChangePaging}
                        onReload={onReload}
                        filterParams={filterParams}
                        onFilterData={onLoadData}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                    />
                </SortDataContext.Provider>
            </div>
        </Wrapper>
    );
};
